import React, { useMemo, useState } from "react";
import { yupToFormErrors } from "formik";
import { toast } from "react-toastify";

import { useAuthContext } from "../../../../lib/context/Auth/AuthContext";
import { useWorkplacesClient } from "../../../../api/grpc/workplaces/useWorkplacesClient";
import { useWorkplaceRequests } from "../../../../api/graphql/useWorkplaceRequests";
import { useDevicesRequests } from "../../../../api/graphql/devices/useDevicesRequests";
import { useEditDeviceFormik } from "../../../shared/Forms/DeviceForm/lib/useEditDeviceFormik";
import { toHardwareInfo } from "../../../shared/Forms/DeviceForm/sections/HardwareSection";
import {
  DateTimeSchema,
  supportsDateTimeSettings,
  toDeviceLocale,
  toFormLocale,
  toFormTimeZone,
} from "../../../Devices/lib/deviceDateTime";

import ModalSidebar from "../../../Modals/ModalSidebar/ModalSidebar";
import {
  DeviceFormFields,
  EditDeviceFormFields,
} from "../../../../api/graphql/workplaces";
import { EditDeviceForm } from "../../../shared/Forms/DeviceForm/EditDeviceForm";
import {
  MAX_DEVICE_TELEMETRY_SENSORS,
  UpdateDeviceVariables,
} from "../../../../api/graphql/devices/devices";
import { DeviceInformation } from "./types";
import { LocationDevice } from "./components/LocationDevice";
import { DeviceModalTab, DeviceModalTabs } from "./components/DeviceModalTabs";
import useGrpcQuery from "../../../../lib/hooks/useGrpcQuery";
import {
  GetDeviceInfoRequest,
  GetDeviceInfoResponse,
} from "../../../../api/grpc/workplaces/workplaces";

import "./DeviceModal.scss";

const DATE_TIME_FIELDS = [
  "timeZone",
  "locale",
  "autoTime",
  "use24HourTime",
] as const;

interface Props {
  isOpen: boolean;
  toggle: () => void;
  data: DeviceFormFields;
  // Reloads the device list after a Date & time save
  refetch?: () => void;
}

export const EditDevice = (props: Props) => {
  const { isOpen, toggle, data, refetch } = props;

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<DeviceModalTab>("settings");

  const { user } = useAuthContext();
  const { getDeviceInfo, updateRDXDevicesDateTimeSettings } =
    useWorkplacesClient();
  const { updateDevice } = useWorkplaceRequests();
  const {
    disconnectDevice,
    disconnectDeviceWayfinder,
    updateDeviceRoom,
    updateDeviceWayfinder,
  } = useDevicesRequests();
  const isRdxDevice = data.typeOfWorkspace === "room";
  const supportsDateTime = supportsDateTimeSettings(data);
  const supportsRestart = !!data.deviceSupportedFeature?.restartEvent;

  const { data: deviceDetails, error: deviceDetailsError } = useGrpcQuery<
    Omit<GetDeviceInfoRequest, "customerId">,
    GetDeviceInfoResponse
  >({
    method: getDeviceInfo,
    variables: {
      deviceId: data.deviceId || "",
    },
  });

  const shouldUpdateDevice = useMemo(() => {
    if (!data.softwareInfo) {
      return false;
    }

    return !!deviceDetails?.isUpdateRequired;
  }, [data.softwareInfo, deviceDetails?.isUpdateRequired]);

  const locationPathItems = useMemo(() => {
    const path = deviceDetails?.locationPatch?.path || [];

    return [...path]
      .sort((a, b) => a.index - b.index)
      .map(({ name }) => name)
      .filter(Boolean);
  }, [deviceDetails?.locationPatch?.path]);

  // Never throws, so a rejected Date & time save doesn't stop the rest of the save
  const saveDateTime = async ({
    timeZone,
    locale,
    autoTime,
    use24HourTime,
  }: EditDeviceFormFields) => {
    try {
      const { response } = await updateRDXDevicesDateTimeSettings({
        deviceIds: [data.deviceId || ""],
        timezone: timeZone ?? "",
        locale: toDeviceLocale(locale ?? "", data.softwareInfo?.rdxGenVersion),
        autoTimeEnabled: !!autoTime,
        format24HEnabled: !!use24HourTime,
      });

      if (response.failedDeviceIds.length) {
        toast.error(
          "Date and time settings couldn't be saved, please try again!"
        );
      }
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.message ||
          "Date and time settings couldn't be saved, please try again!"
      );
    }
  };

  const initialValues: EditDeviceFormFields = {
    name: data.name,
    description: data.description,
    roomId: data?.roomId,
    wayFinderId: data?.wayFinderId,
    autoUpdate: data.autoUpdate,
    // The lists' values, whatever generation or zone name the device reported
    timeZone: toFormTimeZone(data.timezone),
    locale: toFormLocale(data.locale),
    autoTime: data.autoTimeEnabled ?? false,
    use24HourTime: data.format24hEnabled ?? true,
  };

  /*
    Submit callback calls three mutations.
    disconnectDevice & updateDeviceRoom are executed each in certain case, these two mutations are used to trigger the events sent to RDX.
    updateDevice is used to update the other data for that device.
    Changed Date & time values go to the device over gRPC, beside the mutations.
  */
  const formik = useEditDeviceFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values, { setErrors }) => {
      const dateTimeChanged =
        supportsDateTime &&
        DATE_TIME_FIELDS.some(
          (field) => values[field] !== initialValues[field]
        );

      // The modal's schema only checks the name; Date & time follows the wizard's rules
      if (dateTimeChanged) {
        try {
          await DateTimeSchema.validate(values, { abortEarly: false });
        } catch (error) {
          setErrors(yupToFormErrors(error));
          return;
        }
      }

      setLoading(true);

      if (!user) {
        return;
      }

      const dateTimeSaved = dateTimeChanged ? saveDateTime(values) : undefined;

      try {
        const variables: UpdateDeviceVariables = {
          id: data.id || "",
          name: values.name,
          tenantId: user.customerid,
          description: values.description,
          autoUpdate: values.autoUpdate,
        };

        // Unpair the device if the new value is set to null
        if (data.wayFinderId && values.wayFinderId === null) {
          await disconnectDeviceWayfinder({
            id: data.id || "",
            wayFinderId: data.wayFinderId,
            unassignedSince: Math.floor(Date.now() / 1000),
            warnedUnassigned: true,
          });
        }

        if (data.roomId && values.roomId === null) {
          await disconnectDevice({
            id: data.id || "",
            roomId: data.roomId,
            unassignedSince: Math.floor(Date.now() / 1000),
            warnedUnassigned: true,
          });
        }

        if (
          (values.roomId !== null && data.roomId !== values.roomId) ||
          (values.wayFinderId !== null &&
            data.wayFinderId !== values.wayFinderId)
        ) {
          // Update the device room or wayfinder if the new value is different from the initial
          if (!!values.wayFinderId) {
            await updateDeviceWayfinder({
              id: data.id || "",
              wayFinderId: values.wayFinderId || "",
              oldWayfinderId: data.wayFinderId,
              tenantId: user.customerid,
              unassignedSince: 0,
              warnedUnassigned: false,
            });
          } else {
            await updateDeviceRoom({
              id: data.id || "",
              roomId: values.roomId || "",
              oldRoomId: data.roomId,
              tenantId: user.customerid,
              unassignedSince: 0,
              warnedUnassigned: false,
            });
          }
        }

        await updateDevice(variables);
        await dateTimeSaved;

        // After the mutations, so the list can't reload a stale device
        if (dateTimeChanged) {
          refetch?.();
        }

        toggle();
        toast.success("Device edited successfully!");
      } catch (error: any) {
        toast.error(
          error?.message ||
            "Device couldn't be updated, please reload and try again!"
        );
        setLoading(false);
        console.error(error);
      }
    },
  });

  const deviceInfo: DeviceInformation | null = useMemo(() => {
    // Paired devices report hardware/software/sensors even while unassigned
    if (!!data.hardwareInfo && !!data.softwareInfo) {
      const {
        softwareInfo: { androidOS, app },
        hardwareInfo,
        telemetrySensors,
      } = data;
      return {
        softwareInfo: {
          App: app,
        },
        hardwareInfo: toHardwareInfo(hardwareInfo, androidOS),
        sensorInfo: isRdxDevice
          ? (telemetrySensors || [])
              .slice(0, MAX_DEVICE_TELEMETRY_SENSORS)
              .map((sensor) => ({
                "Sensor ID": sensor.sensorId || "-",
                "Sensor MAC": sensor.mac || "-",
                Status: sensor.connectionState || "-",
                "Battery level": sensor.sensorBatteryLevel ?? 0,
              }))
          : [],
        sensorLastSync: telemetrySensors?.[0]?.lastSync ?? null,
      };
    } else {
      return null;
    }
  }, [data, isRdxDevice]);

  const workspaceId = useMemo(() => {
    if (!!!data.wayFinderId && !!!data.roomId) {
      return "Unassigned";
    }

    return !!data.roomId ? data.roomId : data.wayFinderId;
  }, [data]);

  return (
    <ModalSidebar
      isOpen={isOpen}
      loading={loading}
      onToggle={toggle}
      title={data.name}
      className="DeviceModal"
      headerDescription={
        <>
          <LocationDevice
            hasLocation={!!data.roomId || !!data.wayFinderId}
            locationPathItems={locationPathItems}
            locationError={!!deviceDetailsError}
          />
          <DeviceModalTabs activeTab={activeTab} onChange={setActiveTab} />
        </>
      }
      footerDescription={
        formik.dirty ? "Unsaved changes" : "Changes apply when you save."
      }
      onSubmit={formik.handleSubmit}
    >
      <EditDeviceForm
        id={data.id || ""}
        formik={formik}
        isWayfinderDevice={!isRdxDevice}
        deviceInfo={deviceInfo}
        deviceId={data.deviceId || ""}
        workspaceId={workspaceId}
        shouldUpdateDevice={shouldUpdateDevice}
        appVersion={data.softwareInfo?.version}
        isOnline={data.isOnline}
        lastDevicePing={data.lastDevicePing}
        activeTab={activeTab}
        showDateTime={supportsDateTime}
        showRestart={supportsRestart}
        refetch={refetch}
        // Task 10 adds the restart confirm
        onRestart={() => {}}
      />
    </ModalSidebar>
  );
};
